import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, Settings, Gift, Receipt, LogOut, Sparkles, CreditCard, Plus } from 'lucide-react';
import { type AppView } from '../src/routes/slugs';
import { api } from '../services/api';

interface Props {
  user: { firstName: string; lastName?: string | null; companyName: string; credits: number; role: 'beheerder' | 'medewerker' | 'superadmin'; avatarUrl?: string | null };
  onNavigate: (view: AppView) => void;
  onLogout: () => void;
  onOpenSearch: () => void;
}

export const AppTopBar: React.FC<Props> = ({ user, onNavigate, onLogout, onOpenSearch }) => {
  const [platformMac, setPlatformMac] = useState(false);
  useEffect(() => {
    if (typeof navigator !== 'undefined') setPlatformMac(/Mac|iPod|iPhone|iPad/.test(navigator.platform));
  }, []);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const [planLabel, setPlanLabel] = useState<string>('Gratis plan');
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ plan?: string | null; planLabel?: string | null }>('/api/payments/subscription')
      .then((data) => {
        if (cancelled || !data) return;
        const plan = data.planLabel ?? data.plan ?? '';
        if (!plan) return;
        if (/solo/i.test(plan)) setPlanLabel('Solo');
        else if (/kantoor/i.test(plan)) setPlanLabel('Kantoor');
        else if (/gratis|free/i.test(plan)) setPlanLabel('Gratis plan');
        else setPlanLabel(plan);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const lowCredits = user.credits < 10;
  const fullName = `${user.firstName}${user.lastName ? ' ' + user.lastName : ''}`;
  const initials = `${user.firstName?.charAt(0)?.toUpperCase() ?? ''}${user.lastName?.charAt(0)?.toUpperCase() ?? ''}`;

  return (
    <header className="h-14 shrink-0 sticky top-0 z-30 bg-white border-b border-black/8 flex items-center gap-2 md:gap-4 pl-16 pr-3 md:px-5">
      <button
        onClick={onOpenSearch}
        className="flex min-w-0 items-center gap-2 px-2 md:px-3 py-1.5 rounded-lg border border-black/8 bg-black/[0.02] hover:bg-black/[0.04] hover:border-black/12 transition-colors w-full max-w-sm group"
        aria-label="Snelzoeken openen"
      >
        <Search size={14} className="text-ink/45 group-hover:text-ink/65 transition-colors" />
        <span className="flex-1 text-left text-[12.5px] font-medium text-ink/50 group-hover:text-ink/70 transition-colors">Zoeken…</span>
        <span className="hidden sm:flex items-center gap-0.5">
          <kbd className="text-[9.5px] font-semibold text-ink/55 bg-white border border-black/10 rounded px-1 py-[1px]">{platformMac ? '⌘' : 'Ctrl'}</kbd>
          <kbd className="text-[9.5px] font-semibold text-ink/55 bg-white border border-black/10 rounded px-1 py-[1px]">K</kbd>
        </span>
      </button>

      <div className="flex-1" />

      <button
        onClick={() => onNavigate('subscription')}
        className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-black/8 hover:bg-black/[0.04] hover:border-black/12 transition-colors text-[12px] font-semibold text-ink/75"
        title="Beheer abonnement"
      >
        <Sparkles size={13} className="text-ink/55" />
        <span>{planLabel}</span>
      </button>

      <button
        onClick={() => onNavigate('subscription')}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors text-[12px] font-bold ${
          lowCredits
            ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
            : 'border-black/10 bg-black/[0.02] text-ink/75 hover:bg-black/[0.05] hover:border-black/15'
        }`}
        title={lowCredits ? 'Bijna op — koop credits' : 'Credits bijkopen'}
        aria-label={lowCredits ? `${user.credits} credits — bijna op, klik om bij te kopen` : `${user.credits} credits`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${lowCredits ? 'bg-red-500 animate-pulse' : 'bg-ink/55'}`} />
        <span>{user.credits} credits</span>
        {lowCredits && <Plus size={11} className="opacity-70" />}
      </button>

      <div ref={dropdownRef} className="relative">
        <button
          onClick={() => setDropdownOpen(o => !o)}
          className={`flex items-center gap-2 pl-1 pr-1.5 py-1 rounded-lg transition-colors ${dropdownOpen ? 'bg-black/5' : 'hover:bg-black/4'}`}
          aria-haspopup="menu"
          aria-expanded={dropdownOpen}
        >
          <div className="w-8 h-8 rounded-full bg-ink text-white flex items-center justify-center overflow-hidden shadow-[0_2px_6px_-2px_rgba(0,0,0,0.2)] shrink-0">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt={`Profielfoto van ${user.firstName || 'gebruiker'}`} className="w-full h-full object-cover" />
            ) : (
              <span className="text-[11px] font-bold leading-none">{initials}</span>
            )}
          </div>
          <div className="hidden lg:block text-left leading-tight">
            <p className="text-[12.5px] font-bold text-ink truncate max-w-[140px]">{fullName}</p>
            <p className="text-[10.5px] font-medium text-ink/55 truncate max-w-[140px]">{user.companyName || <span className="capitalize">{user.role}</span>}</p>
          </div>
          <ChevronDown size={14} className={`text-ink/40 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {dropdownOpen && (
          <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-black/8 rounded-xl shadow-[0_14px_40px_-12px_rgba(0,0,0,0.18)] overflow-hidden z-50">
            <div className="px-4 py-3 border-b border-black/6">
              <p className="text-[13px] font-bold text-ink truncate">{fullName}</p>
              <p className="text-[11px] font-medium text-ink/55 truncate capitalize">{user.role}</p>
            </div>
            <div className="py-1.5">
              <DropdownItem icon={<Settings size={14} />} label="Profiel bewerken" onClick={() => { onNavigate('profile'); setDropdownOpen(false); }} />
              <DropdownItem icon={<Gift size={14} />} label="Referrals" onClick={() => { onNavigate('referrals'); setDropdownOpen(false); }} />
              <DropdownItem icon={<CreditCard size={14} />} label="Abonnement" onClick={() => { onNavigate('subscription'); setDropdownOpen(false); }} />
              <DropdownItem icon={<Receipt size={14} />} label="Betalingen" onClick={() => { onNavigate('subscription'); setDropdownOpen(false); }} />
            </div>
            <div className="border-t border-black/6 py-1.5">
              <button
                onClick={() => { onLogout(); setDropdownOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors text-left"
              >
                <LogOut size={14} />
                Uitloggen
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

const DropdownItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm font-medium text-ink/70 hover:bg-black/4 hover:text-ink transition-colors text-left"
  >
    {icon}
    {label}
  </button>
);
